/**
 * Z-Index 管理器
 * 用于管理弹窗的层级，确保后面打开的弹窗层级更高
 */

class ZIndexManager {
  constructor() {
    this.baseZIndex = 1000; // 基础 z-index 值
    this.currentZIndex = this.baseZIndex;
    this.activeModals = new Map(); // 存储活跃的弹窗及其 z-index
  }

  /**
   * 获取下一个可用的 z-index
   * @returns {number} z-index 值
   */
  getNextZIndex() {
    this.currentZIndex += 10; // 每次增加 10，确保层级差异明显
    return this.currentZIndex;
  }

  /**
   * 注册弹窗
   * @param {string} modalId - 弹窗唯一标识
   * @param {string} modalName - 弹窗名称（用于调试）
   * @returns {number} 分配的 z-index 值
   */
  registerModal(modalId, modalName = "") {
    const zIndex = this.getNextZIndex();
    this.activeModals.set(modalId, {
      zIndex,
      name: modalName,
      timestamp: Date.now(),
    });

    return zIndex;
  }

  /**
   * 注销弹窗
   * @param {string} modalId - 弹窗唯一标识
   */
  unregisterModal(modalId) {
    const modal = this.activeModals.get(modalId);
    if (modal) {
      console.log(
        `[ZIndexManager] 注销弹窗: ${modal.name} (${modalId}), z-index: ${modal.zIndex}`
      );
      this.activeModals.delete(modalId);
    }
  }

  /**
   * 获取弹窗的 z-index
   * @param {string} modalId - 弹窗唯一标识
   * @returns {number} z-index 值，如果不存在则返回基础值
   */
  getModalZIndex(modalId) {
    const modal = this.activeModals.get(modalId);
    return modal ? modal.zIndex : this.baseZIndex;
  }

  /**
   * 提升弹窗层级（重新激活）
   * @param {string} modalId - 弹窗唯一标识
   * @param {string} modalName - 弹窗名称
   * @returns {number} 新的 z-index 值
   */
  bringToFront(modalId, modalName = "") {
    // 先注销旧的
    this.unregisterModal(modalId);
    // 重新注册，获得更高的 z-index
    return this.registerModal(modalId, modalName);
  }

  /**
   * 获取所有活跃弹窗信息
   * @returns {Array} 弹窗信息数组
   */
  getActiveModals() {
    return Array.from(this.activeModals.entries()).map(([id, info]) => ({
      id,
      name: info.name,
      zIndex: info.zIndex,
      timestamp: info.timestamp,
    }));
  }

  /**
   * 重置管理器
   */
  reset() {
    this.currentZIndex = this.baseZIndex;
    this.activeModals.clear();
    console.log("[ZIndexManager] 重置管理器");
  }
}

// 创建全局单例
const zIndexManager = new ZIndexManager();

// 简单的测试，确保模块正常工作
console.log("[ZIndexManager] 模块已加载");

// 添加全局测试函数
if (typeof window !== "undefined") {
  window.testZIndexManager = () => {
    console.log("=== 测试 Z-Index 管理器 ===");

    // 测试注册弹窗
    const z1 = zIndexManager.registerModal("test1", "测试弹窗1");
    const z2 = zIndexManager.registerModal("test2", "测试弹窗2");
    const z3 = zIndexManager.registerModal("test3", "测试弹窗3");

    console.log("弹窗1 z-index:", z1);
    console.log("弹窗2 z-index:", z2);
    console.log("弹窗3 z-index:", z3);

    // 测试提升层级
    const newZ1 = zIndexManager.bringToFront("test1", "测试弹窗1");
    console.log("弹窗1 提升后的 z-index:", newZ1);

    // 显示活跃弹窗
    console.log("活跃弹窗:", zIndexManager.getActiveModals());

    // 清理测试
    zIndexManager.unregisterModal("test1");
    zIndexManager.unregisterModal("test2");
    zIndexManager.unregisterModal("test3");

    console.log("测试完成");
  };

  console.log(
    "[ZIndexManager] 测试函数已加载，可在控制台调用 testZIndexManager()"
  );
}

export default zIndexManager;
